import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, assetUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PasswordInput } from "@/components/ui/password-input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Save, KeyRound, UserCog, ImageIcon, Upload } from "lucide-react";
import { toast } from "sonner";
import { BUSINESS_TYPES } from "@/lib/businessTypes";
import { CitySelect } from "@/components/CitySelect";

const INITIAL_PROFILE = {
  name: "", business: "", business_type: "Other", business_type_other: "",
  city: "", phone: "", email: "", gst_number: "", billing_address: "",
  default_logo_url: "", address: "",
};

export default function SettingsPage() {
  const { vendor, refresh } = useAuth();
  const [profile, setProfile] = useState(INITIAL_PROFILE);
  const [savingProfile, setSavingProfile] = useState(false);

  const [pwd, setPwd] = useState({
    current_password: "", new_password: "", confirm: "",
  });
  const [savingPwd, setSavingPwd] = useState(false);

  // Hydrate the form from vendor whenever it loads/changes
  useEffect(() => {
    if (!vendor) return;
    setProfile({
      name: vendor.name || "",
      business: vendor.business || "",
      business_type: vendor.business_type || "Other",
      business_type_other: vendor.business_type_other || "",
      city: vendor.city || "",
      phone: vendor.phone || "",
      email: vendor.email || "",
      gst_number: vendor.gst_number || "",
      billing_address: vendor.billing_address || "",
      default_logo_url: vendor.default_logo_url || "",
      address: vendor.address || "",
    });
  }, [vendor]);

  const onProfileChange = (k, v) =>
    setProfile((p) => {
      const next = { ...p, [k]: v };
      // Clear the free-text field the moment the vendor picks a real
      // category — matches the register-form behaviour and prevents
      // stale data from being sent to the server.
      if (k === "business_type" && v !== "Other") {
        next.business_type_other = "";
      }
      return next;
    });

  const saveProfile = async (e) => {
    e.preventDefault();
    // Client-side "Other requires text" check — surfaces the error
    // before a round-trip; server enforces the same rule.
    if (
      profile.business_type === "Other" &&
      !profile.business_type_other.trim()
    ) {
      toast.error("Please specify your business category.");
      return;
    }
    setSavingProfile(true);
    try {
      const payload = {
        name: profile.name?.trim(),
        business: profile.business?.trim(),
        business_type: profile.business_type,
        business_type_other:
          profile.business_type === "Other"
            ? profile.business_type_other.trim()
            : null,
        city: profile.city?.trim(),
        phone: profile.phone?.trim(),
        email: profile.email?.trim(),
        gst_number: profile.gst_number?.trim() || null,
        billing_address: profile.billing_address?.trim() || null,
        default_logo_url: profile.default_logo_url?.trim() || null,
        address: profile.address?.trim() || null,
      };
      await api.updateProfile(payload);
      await refresh();
      toast.success("Profile updated successfully");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (pwd.new_password !== pwd.confirm) {
      toast.error("New password and confirmation do not match");
      return;
    }
    setSavingPwd(true);
    try {
      await api.updatePassword({
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      toast.success("Password updated. Use it on your next login.");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update password");
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="vendor-settings-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold">Settings</h1>
        <p className="text-muted-foreground text-sm">
          Manage your business profile and login credentials.
        </p>
      </div>

      {/* Profile card */}
      <Card className="p-6 max-w-3xl space-y-5">
        <div className="flex items-center gap-2">
          <UserCog className="h-5 w-5 text-primary" />
          <h2 className="font-heading text-lg font-semibold">Profile</h2>
        </div>
        <form onSubmit={saveProfile} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="s-name">Full name</Label>
            <Input
              id="s-name"
              value={profile.name}
              onChange={(e) => onProfileChange("name", e.target.value)}
              data-testid="settings-name-input"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="s-business">Business name</Label>
            <Input
              id="s-business"
              value={profile.business}
              onChange={(e) => onProfileChange("business", e.target.value)}
              data-testid="settings-business-input"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label>Business type</Label>
            <Select
              value={profile.business_type}
              onValueChange={(v) => onProfileChange("business_type", v)}
            >
              <SelectTrigger data-testid="settings-business-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BUSINESS_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {profile.business_type === "Other" && (
            <div className="space-y-1.5">
              <Label htmlFor="s-business-type-other">
                Please specify your business category
              </Label>
              <Input
                id="s-business-type-other"
                value={profile.business_type_other}
                onChange={(e) => onProfileChange("business_type_other", e.target.value)}
                data-testid="settings-business-type-other-input"
                placeholder="e.g. Pet supplies, Bookstore, Tiffin service"
                maxLength={80}
                required
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="s-city">City</Label>
            <CitySelect
              value={profile.city}
              onChange={(name) => onProfileChange("city", name)}
              placeholder="Select your city…"
              testIdPrefix="settings-city"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="s-phone">Phone</Label>
            <Input
              id="s-phone"
              value={profile.phone}
              onChange={(e) => onProfileChange("phone", e.target.value)}
              data-testid="settings-phone-input"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="s-email">Email</Label>
            <Input
              id="s-email"
              type="email"
              value={profile.email}
              onChange={(e) => onProfileChange("email", e.target.value)}
              data-testid="settings-email-input"
              required
            />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="s-gst">GST number (optional)</Label>
            <Input
              id="s-gst"
              value={profile.gst_number}
              onChange={(e) => onProfileChange("gst_number", e.target.value)}
              data-testid="settings-gst-input"
              placeholder="22AAAAA0000A1Z5"
            />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="s-billing-address">Billing address (optional)</Label>
            <Textarea
              id="s-billing-address"
              value={profile.billing_address}
              onChange={(e) => onProfileChange("billing_address", e.target.value)}
              data-testid="settings-billing-address-input"
              placeholder="Shop no., street, area, city, state, PIN — appears on your invoices"
              rows={3}
              maxLength={500}            />
            <p className="text-xs text-muted-foreground">
              Used on the tax invoices we generate for your wallet top-ups. Leave blank to omit.
            </p>
          </div>
          {/* 02/08 — Store address shown to a customer AFTER they claim
              a Static Offer. Purely for redemption directions. NOT
              surfaced on anonymous public browsing. Decoupled from the
              deferred lat/lng geocoding work. */}
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="s-store-address">Store address (shown after a customer claims your Static Offer)</Label>
            <Textarea
              id="s-store-address"
              value={profile.address}
              onChange={(e) => onProfileChange("address", e.target.value)}
              data-testid="settings-store-address-input"
              placeholder="Shop no., landmark, area, city — helps customers who just won find your store"
              rows={2}
              maxLength={300}
            />
            <p className="text-xs text-muted-foreground">
              Only visible to a customer AFTER a successful claim, alongside your business name and phone. Anonymous browsers on Explore never see this.
            </p>
          </div>
          {/*
            Default brand logo — new in Pass 2 (A6).
            Auto-fills `logo_url` on every NEW campaign so a
            brand-consistent vendor doesn't have to re-upload the same
            image per campaign. The per-campaign upload in the wizard
            remains editable; existing campaigns keep whatever they
            were saved with.
          */}
          <div className="space-y-1.5 md:col-span-2">
            <Label className="flex items-center gap-1.5">
              <ImageIcon className="h-4 w-4 opacity-70" /> Default campaign logo (optional)
            </Label>
            <div className="flex items-start gap-3 flex-wrap">
              {profile.default_logo_url ? (
                <img
                  src={assetUrl(profile.default_logo_url)}
                  alt="Default logo preview"
                  className="h-16 w-16 rounded-lg object-cover border-2 border-[#0A0A0A]"
                  data-testid="settings-default-logo-preview"
                />
              ) : (
                <div className="h-16 w-16 rounded-lg bg-[#F7F4EC] border-2 border-dashed border-[#0A0A0A]/30 flex items-center justify-center text-[#0A0A0A]/40">
                  <ImageIcon className="h-6 w-6" />
                </div>
              )}
              <label className="inline-flex items-center gap-2 cursor-pointer text-xs px-3 py-2 rounded-lg border-2 border-[#0A0A0A] bg-white hover:bg-[#F5A800] transition-colors">
                <Upload className="h-3.5 w-3.5" />
                {profile.default_logo_url ? "Replace" : "Upload logo"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="settings-default-logo-upload"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (!f) return;
                    try {
                      const r = await api.upload(f);
                      const url = r.data?.url || r.data?.path;
                      if (url) {
                        onProfileChange("default_logo_url", url);
                        toast.success("Default logo uploaded");
                      } else {
                        toast.error("Upload succeeded but no URL returned");
                      }
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Upload failed");
                    }
                  }}
                />
              </label>
              {profile.default_logo_url && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onProfileChange("default_logo_url", "")}
                  data-testid="settings-default-logo-clear"
                >
                  Remove
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Auto-fills the logo on every new campaign you create. You can still override
              per campaign in the wizard.
            </p>
          </div>
          <div className="md:col-span-2 flex justify-end pt-2">
            <Button
              type="submit"
              disabled={savingProfile}
              data-testid="settings-save-profile-button"
            >
              {savingProfile ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save profile
            </Button>
          </div>
        </form>
      </Card>

      {/* Password card */}
      <Card className="p-6 max-w-3xl space-y-5">
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-primary" />
          <h2 className="font-heading text-lg font-semibold">Change password</h2>
        </div>
        <form onSubmit={savePassword} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1.5 md:col-span-3">
            <Label htmlFor="s-current">Current password</Label>
            <PasswordInput
              id="s-current"
              autoComplete="current-password"
              value={pwd.current_password}
              onChange={(e) => setPwd((p) => ({ ...p, current_password: e.target.value }))}
              data-testid="settings-current-password-input"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="s-new">New password</Label>
            <PasswordInput
              id="s-new"
              autoComplete="new-password"
              value={pwd.new_password}
              onChange={(e) => setPwd((p) => ({ ...p, new_password: e.target.value }))}
              data-testid="settings-new-password-input"
              required
              minLength={6}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="s-confirm">Confirm new password</Label>
            <PasswordInput
              id="s-confirm"
              autoComplete="new-password"
              value={pwd.confirm}
              onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
              data-testid="settings-confirm-password-input"
              required
            />
          </div>
          <div className="md:col-span-3 flex justify-end pt-2">
            <Button
              type="submit"
              variant="outline"
              disabled={savingPwd}
              data-testid="settings-save-password-button"
            >
              {savingPwd ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <KeyRound className="h-4 w-4 mr-2" />
              )}
              Update password
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
